import React, { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { BLOG_ARTICLES, BLOG_LIST } from "@/data/blogArticles";
import { Clock, ArrowRight, ChevronDown, ArrowLeft, BookOpen } from "lucide-react";

// Simple markdown-lite : bold **text**
function renderInline(text) {
    if (!text) return null;
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, i) =>
        p.startsWith("**") && p.endsWith("**") ? (
            <strong key={i} className="text-[#0B192C]">{p.slice(2, -2)}</strong>
        ) : (
            <React.Fragment key={i}>{p}</React.Fragment>
        )
    );
}

function useArticleSEO(article) {
    useEffect(() => {
        if (!article) return;
        document.title = article.title;
        const setMeta = (name, content, attr = "name") => {
            let el = document.querySelector(`meta[${attr}="${name}"]`);
            if (!el) {
                el = document.createElement("meta");
                el.setAttribute(attr, name);
                document.head.appendChild(el);
            }
            el.setAttribute("content", content);
        };
        setMeta("description", article.meta_desc);
        setMeta("og:title", article.title);
        setMeta("og:description", article.meta_desc);
        setMeta("og:type", "article");
        setMeta("og:url", `https://renforteo.fr/blog/${article.slug}`);
        setMeta("og:image", article.hero_img?.startsWith("http") ? article.hero_img : `https://renforteo.fr${article.hero_img || ""}`);
        setMeta("article:published_time", article.published_at, "property");
        setMeta("article:section", article.category, "property");

        let canon = document.querySelector("link[rel=canonical]");
        if (!canon) {
            canon = document.createElement("link");
            canon.setAttribute("rel", "canonical");
            document.head.appendChild(canon);
        }
        canon.setAttribute("href", `https://renforteo.fr/blog/${article.slug}`);

        // JSON-LD BlogPosting + FAQPage
        const jsonld = {
            "@context": "https://schema.org",
            "@graph": [
                {
                    "@type": "BlogPosting",
                    headline: article.h1,
                    description: article.meta_desc,
                    image: article.hero_img?.startsWith("http") ? article.hero_img : `https://renforteo.fr${article.hero_img || ""}`,
                    datePublished: article.published_at,
                    dateModified: article.published_at,
                    articleSection: article.category,
                    author: {
                        "@type": "Organization",
                        name: "Renforteo",
                        url: "https://renforteo.fr",
                    },
                    publisher: {
                        "@type": "Organization",
                        name: "Renforteo",
                        logo: {
                            "@type": "ImageObject",
                            url: "https://renforteo.fr/logo.png",
                        },
                    },
                    mainEntityOfPage: {
                        "@type": "WebPage",
                        "@id": `https://renforteo.fr/blog/${article.slug}`,
                    },
                    keywords: (article.keywords || []).join(", "),
                },
                ...(article.faqs && article.faqs.length
                    ? [
                          {
                              "@type": "FAQPage",
                              mainEntity: article.faqs.map((f) => ({
                                  "@type": "Question",
                                  name: f.q,
                                  acceptedAnswer: { "@type": "Answer", text: f.a },
                              })),
                          },
                      ]
                    : []),
            ],
        };
        let ld = document.getElementById("blog-article-jsonld");
        if (!ld) {
            ld = document.createElement("script");
            ld.type = "application/ld+json";
            ld.id = "blog-article-jsonld";
            document.head.appendChild(ld);
        }
        ld.textContent = JSON.stringify(jsonld);
    }, [article]);
}

function Section({ s, index }) {
    if (s.type === "h2") {
        return (
            <h2
                className="font-display text-2xl md:text-3xl text-[#0B192C] tracking-tight mt-10 mb-4"
                data-testid={`article-h2-${index}`}
            >
                {s.text}
            </h2>
        );
    }
    if (s.type === "h3") {
        return (
            <h3 className="font-display text-xl text-[#0B192C] mt-8 mb-3">
                {s.text}
            </h3>
        );
    }
    if (s.type === "p") {
        return (
            <p className="text-slate-700 leading-relaxed mb-5" data-testid={`article-p-${index}`}>
                {renderInline(s.text)}
            </p>
        );
    }
    if (s.type === "ul") {
        return (
            <ul className="list-disc pl-6 space-y-2 mb-6 text-slate-700 leading-relaxed">
                {s.items.map((it, i) => (
                    <li key={i}>{renderInline(it)}</li>
                ))}
            </ul>
        );
    }
    if (s.type === "ol") {
        return (
            <ol className="list-decimal pl-6 space-y-2 mb-6 text-slate-700 leading-relaxed">
                {s.items.map((it, i) => (
                    <li key={i}>{renderInline(it)}</li>
                ))}
            </ol>
        );
    }
    if (s.type === "quote") {
        return (
            <blockquote className="border-l-4 border-[#F5A623] pl-5 py-2 my-6 italic text-slate-600">
                {s.text}
            </blockquote>
        );
    }
    if (s.type === "cta") {
        return (
            <div
                className="my-10 rounded-2xl border border-[#F5A623]/40 bg-[#F5A623]/10 p-6 md:p-7"
                data-testid={`article-cta-${index}`}
            >
                <Link
                    to={s.to}
                    className="btn-gold text-sm md:text-base inline-block"
                    data-testid="article-cta-link"
                >
                    {s.label}
                    <ArrowRight size={16} className="inline ml-2 -mt-0.5" />
                </Link>
                {s.subtext && (
                    <p className="text-xs text-slate-600 mt-3 leading-relaxed">
                        {s.subtext}
                    </p>
                )}
            </div>
        );
    }
    return null;
}

export default function BlogArticlePage() {
    const { slug } = useParams();
    const article = BLOG_ARTICLES[slug];
    useArticleSEO(article);

    if (!article) {
        return <Navigate to="/blog" replace />;
    }

    const related = BLOG_LIST.filter((a) => a.slug !== slug).slice(0, 3);
    const publishedDate = new Date(article.published_at).toLocaleDateString("fr-FR", {
        day: "numeric",
        month: "long",
        year: "numeric",
    });

    return (
        <div data-testid="blog-article-page">
            <Header />

            {/* Hero image */}
            <section className="relative bg-[#0B192C]" data-testid="article-hero">
                <div
                    className="absolute inset-0 opacity-40 bg-cover bg-center"
                    style={{ backgroundImage: `url(${article.hero_img})` }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B192C] via-[#0B192C]/70 to-transparent" />
                <div className="relative max-w-4xl mx-auto px-6 md:px-10 pt-24 pb-16 md:pt-32 md:pb-20 text-white">
                    <Link
                        to="/blog"
                        className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-[#F5A623] mb-6 transition-colors"
                        data-testid="article-back-to-blog"
                    >
                        <ArrowLeft size={14} />
                        Retour au blog
                    </Link>
                    <div className="inline-flex items-center gap-2 rounded-full border border-[#F5A623]/40 bg-[#F5A623]/10 px-3 py-1 mb-5">
                        <BookOpen size={12} className="text-[#F5A623]" />
                        <span className="text-[10px] uppercase tracking-widest font-bold text-[#F5A623]">
                            {article.category}
                        </span>
                    </div>
                    <h1
                        className="font-display text-3xl md:text-4xl lg:text-5xl leading-tight tracking-tight mb-5"
                        data-testid="article-h1"
                    >
                        {article.h1}
                    </h1>
                    <div className="flex items-center gap-4 text-xs text-white/60">
                        <span>{publishedDate}</span>
                        <span className="inline-flex items-center gap-1.5">
                            <Clock size={12} />
                            {article.read_time_min} min de lecture
                        </span>
                    </div>
                </div>
            </section>

            {/* Body */}
            <article
                className="max-w-3xl mx-auto px-6 md:px-10 py-14 md:py-20 prose-slate"
                data-testid="article-body"
            >
                <p className="text-lg md:text-xl text-slate-600 leading-relaxed mb-8 font-medium border-l-4 border-[#F5A623] pl-5">
                    {article.excerpt}
                </p>

                {article.sections.map((s, i) => (
                    <Section key={i} s={s} index={i} />
                ))}

                {/* FAQ */}
                {article.faqs && article.faqs.length > 0 && (
                    <div className="mt-16 pt-10 border-t border-slate-200" data-testid="article-faq">
                        <h2 className="font-display text-2xl md:text-3xl text-[#0B192C] tracking-tight mb-6">
                            Questions fréquentes
                        </h2>
                        <div className="space-y-3">
                            {article.faqs.map((f, i) => (
                                <details
                                    key={i}
                                    className="sd-card p-5 group"
                                    data-testid={`article-faq-item-${i}`}
                                >
                                    <summary className="cursor-pointer list-none flex items-start justify-between gap-4">
                                        <span className="font-display text-base md:text-lg text-[#0B192C]">
                                            {f.q}
                                        </span>
                                        <ChevronDown
                                            size={20}
                                            className="shrink-0 text-slate-400 mt-1 transition-transform group-open:rotate-180"
                                        />
                                    </summary>
                                    <p className="text-sm text-slate-600 leading-relaxed mt-3">
                                        {f.a}
                                    </p>
                                </details>
                            ))}
                        </div>
                    </div>
                )}
            </article>

            {/* Related articles */}
            {related.length > 0 && (
                <section className="bg-slate-50 border-t border-slate-200" data-testid="article-related">
                    <div className="max-w-6xl mx-auto px-6 md:px-10 py-14 md:py-16">
                        <h2 className="font-display text-2xl md:text-3xl text-[#0B192C] tracking-tight mb-8 text-center">
                            À lire aussi
                        </h2>
                        <div className="grid md:grid-cols-3 gap-5">
                            {related.map((a) => (
                                <Link
                                    key={a.slug}
                                    to={`/blog/${a.slug}`}
                                    className="group bg-white rounded-2xl border border-slate-200 overflow-hidden hover:border-[#F5A623] transition-colors"
                                    data-testid={`article-related-${a.slug}`}
                                >
                                    <div className="aspect-[16/9] overflow-hidden bg-slate-100">
                                        <img
                                            src={a.hero_img}
                                            alt={a.h1}
                                            loading="lazy"
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                    <div className="p-4">
                                        <div className="text-[10px] uppercase tracking-widest font-bold text-[#F5A623] mb-2">
                                            {a.category}
                                        </div>
                                        <div className="font-display text-base leading-tight text-[#0B192C] group-hover:text-[#F5A623] transition-colors">
                                            {a.h1}
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            <Footer />
        </div>
    );
}
